import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { LayoutDashboard, Plus, Wallet, ShieldCheck, Sun, Moon, LogOut, Building2 } from 'lucide-react';
import Logo from '@/components/Logo';

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', matchPath: '/campaigns' },
  { label: 'Business', icon: Building2, path: '/business-profile' },
  { label: 'Create Ad', icon: Plus, path: '/create-ad', isCta: true },
  { label: 'Wallet', icon: Wallet, path: '/wallet' },
];
const isActive = (item, pathname) => pathname === item.path || (item.matchPath && pathname.startsWith(item.matchPath));

const adminNavItem = { label: 'Admin', icon: ShieldCheck, path: '/admin' };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const items = isAdmin ? [...navItems, adminNavItem] : navItems;

  return (
    <div className="min-h-screen bg-background dot-grid">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r-2 border-ink bg-card fixed top-0 left-0 z-40 h-screen" data-testid="desktop-sidebar">
        <div className="p-5 border-b-2 border-ink">
          <Link to="/dashboard" aria-label="Rayogent dashboard"><Logo /></Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map(item => {
            const Icon = item.icon;
            const active = isActive(item, location.pathname);
            return (
              <Link key={item.path + item.label} to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all duration-150 ${
                  item.isCta ? 'gradient-bg mt-4 justify-center'
                    : active ? 'bg-butter border-ink shadow-neo-sm text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-butter/60'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="w-4.5 h-4.5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t-2 border-ink space-y-1">
          <button onClick={toggleTheme} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-butter/60 transition-all w-full">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-700 dark:text-red-400 hover:bg-red-500/10 transition-all w-full" data-testid="logout-button">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-background border-b-2 border-ink flex items-center justify-between px-4" data-testid="mobile-header">
        <Link to="/dashboard" aria-label="Rayogent dashboard"><Logo size="sm" /></Link>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-butter transition-all" aria-label="Switch light or dark mode" data-testid="theme-toggle-button">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button onClick={logout} className="p-2 rounded-lg hover:bg-butter transition-all text-red-700 dark:text-red-400" aria-label="Log out">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16 pb-20 md:pb-8 md:pl-64 md:pt-0">
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 h-16 bg-card border-t-2 border-ink flex items-center justify-around px-2" data-testid="mobile-bottom-nav">
        {items.map(item => {
          const Icon = item.icon;
          const active = isActive(item, location.pathname);
          return (
            <Link key={item.path + item.label} to={item.path} aria-current={active ? 'page' : undefined}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${
                item.isCta
                  ? 'gradient-bg rounded-2xl px-5 -mt-6'
                  : active ? 'text-foreground font-semibold' : 'text-muted-foreground'
              }`}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <Icon className={`${item.isCta ? 'w-5 h-5' : 'w-5 h-5'}`} />
              <span className="text-[11px] font-semibold">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
